import { useForm, usePage } from '@inertiajs/react';
import React, { useRef, useState } from 'react';
import useRoute from '@/Hooks/useRoute';
import Button from '@/Components/Buttons/Button';
import FormSection from '@/Blocks/Forms/FormSection';
import Input from '@/Components/Inputs/Input';
import InputFileUpload from '@/Components/Inputs/InputFileUpload';
import { useToasts } from '../../Hooks/toastContextProvider';

export default function UpdateProfileInformationForm({ user }) {
  const form = useForm({
    _method: 'PUT',
    name: user.name,
    user_role: user.user_role,
    email: user.email,
    photo: user.linkedin_qr_code_photo_url,
  });

  const route = useRoute();
  const [photoPreview, setPhotoPreview] = useState({});
  const photoRef = useRef(null);
  const page = usePage();
  const { addToast } = useToasts();

  const verifyUploadSize = () => {
    const uploadFieldID = 'profilePhoto';
    const MaxSizeInBytes = 1048576;
    const fld = document.getElementById(uploadFieldID);
    if (
      fld.files &&
      fld.files.length == 1 &&
      fld.files[0].size > MaxSizeInBytes
    ) {
      addToast({
        message: `The file size must be no more than ${parseInt(
          MaxSizeInBytes / 1024 / 1024,
        )} MB`,
        variant: 'warning',
      });
      return false;
    }
    return true;
  };

  function updateProfileInformation() {
    form.post(route('user-profile-information.update'), {
      errorBag: 'updateProfileInformation',
      preserveScroll: true,
      onSuccess: () => resetFileInput(),
    });
  }

  function selectNewPhoto() {
    photoRef.current.click();
  }

  const resetFileInput = () => {
    addToast({ message: 'Updated Successfully!', variant: 'success' });
    setPhotoPreview({});

    if (photoRef.current.value) {
      photoRef.current.value = '';
      form.setData('photo', null);
    }
  };

  const updatePhotoPreview = () => {
    const photo = photoRef.current.files[0];

    if (!photo) {
      return;
    }

    if (verifyUploadSize() === true) {
      form.setData('photo', photo);

      const reader = new FileReader();

      reader.onload = e => {
        setPhotoPreview(e.target.result);
      };

      reader.readAsDataURL(photo);
    } else {
      setPhotoPreview({});

      if (photoRef.current.value) {
        photoRef.current.value = '';
        form.setData('photo', null);
      }
    }
  };

  return (
    <FormSection
      onSubmit={updateProfileInformation}
      title={'Profile Information'}
      description={`Add links to your madeby.studio social profile.`}
      renderActions={() => (
        <div className="col-span-2">
          <Button
            disabled={form.processing}
            type="submit"
            style="admin"
            variant="dark"
          >
            <p className="w-full text-lg text-white">Save Profile</p>
          </Button>
        </div>
      )}
    >
      {/* <!-- Name --> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          id="name"
          label="Name"
          type="text"
          value={form.data.name}
          onChange={e => form.setData('name', e.currentTarget.value)}
          autoComplete="name"
          error={form.errors.name}
        />
      </div>
      {/* <!-- Role --> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          id="Job Role"
          label="Job Role"
          type="text"
          value={form.data.user_role}
          onChange={e => form.setData('user_role', e.currentTarget.value)}
          error={form.errors.name}
        />
      </div>
      {/* <!-- Email --> */}
      <div className="col-span-2">
        <Input
          label="Email"
          id="email"
          type="email"
          required={true}
          value={form.data.email}
          onChange={e => form.setData('email', e.currentTarget.value)}
          error={form.errors.email}
        />
      </div>
      {/* <!-- Profile Photo --> */}
      {page.props.jetstream.managesProfilePhotos ? (
        <div className="col-span-2 mb-6">
          <div className="flex items-center">
            <div className="mr-6">
              {Object.keys(photoPreview).length === 0 ? (
                // <!-- New Profile Photo Preview -->
                <div className="mt-2 w-20">
                  <img
                    src={user.profile_photo_url || '/assets/mb.png'}
                    alt={user.name}
                    className="aspect-square w-20 rounded-full object-cover"
                  />
                </div>
              ) : (
                // <!-- Current Profile Photo -->

                <div className="mt-2">
                  <span
                    className="block aspect-square w-20 rounded-full"
                    style={{
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center center',
                      backgroundImage: `url('${photoPreview}')`,
                    }}
                  ></span>
                </div>
              )}
            </div>
            {/* <!-- Profile Photo File Input --> */}
            <InputFileUpload
              type="file"
              className="hidden"
              ref={photoRef}
              id="profilePhoto"
              onChange={updatePhotoPreview}
              onClick={() => selectNewPhoto}
              label="Upload Profile Picture"
              placeholder={
                Object.keys(photoPreview).length === 0 ? (
                  <span className="whitespace-nowrap">
                    {user.profile_photo_url}
                  </span>
                ) : (
                  <span className="whitespace-nowrap">
                    {photoRef.current.files[0].name}
                  </span>
                )
              }
            />
          </div>
        </div>
      ) : null}
    </FormSection>
  );
}
